import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LocationWithState } from 'types/location'

import { api, usePostAuthLoginMutation } from '../../../api'
import { PATH } from '../../../const'

interface Props {
  on2FANotSetup?: () => void
  redirectOnLockedOut: boolean
  redirectOnSuccess: boolean
}
export const useLogin = ({ on2FANotSetup, redirectOnLockedOut, redirectOnSuccess }: Props) => {
  const location = useLocation()
  const [
    postLogin,
    { error: postAuthError, isError, isLoading: isPostAuthLoading, isSuccess: isPostAuthSuccess }
  ] = usePostAuthLoginMutation()
  const [triggerGetUser, { data: user, isLoading: isGetUserLoading, isSuccess: isGetUserSuccess }] =
    api.useLazyGetUsermanagerCurrentQuery()
  const navigate = useNavigate()

  // 1. Fire post login request
  const onSubmit = async ({ password, username }) => {
    const response = await postLogin({ apiLoginUser: { username, password, rememberMe: true } })
    const isSuccess = response.hasOwnProperty('data')

    if (isSuccess) {
      const successResponse = response as any

      if (successResponse.data?.needs2FASetup) {
        // 2a. User has not set up 2FA
        if (on2FANotSetup) {
          on2FANotSetup()
        } else {
          navigate(PATH.twoFactorNotSetUp)
        }
      } else if (successResponse.data?.twoFactorLoginUrl) {
        // 2b. User needs to enter 2FA code
        navigate(PATH.twoFactor)
      } else {
        // 2c. User can log in. Fetch the user data
        triggerGetUser()
      }
    }

    return response
  }

  useEffect(() => {
    // 3. When we have the user data, redirect the user
    if (user && redirectOnSuccess) {
      const redirectPath = (location.state as any)?.from
        ? (location as LocationWithState).state.from.pathname
        : PATH.home

      navigate(redirectPath)
    }
  }, [location, navigate, user, redirectOnSuccess])

  useEffect(() => {
    // 4. If the user is locked out, redirect the user
    if (redirectOnLockedOut && (postAuthError as any)?.originalStatus === 423) {
      navigate(PATH.accountLocked)
    }
    if (redirectOnLockedOut && (postAuthError as any)?.originalStatus === 403) {
      navigate(PATH.accountInactive)
    }
  }, [postAuthError, redirectOnLockedOut, navigate])

  const isLoading = isPostAuthLoading || isGetUserLoading
  const isSuccess = isPostAuthSuccess && isGetUserSuccess

  return [onSubmit, { isError, isLoading, isSuccess, error: postAuthError }] as const
}
